import { auth } from '../firebase'; // adjust ONLY if your app uses a different firebase export

function getBackendUrl() {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin;
  }

  return window.location.origin;
}

async function getAuthHeaders() {
  const user = auth.currentUser;
  if (!user) return {};

  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchPricingConfig(service) {
  const backendUrl = getBackendUrl();
  const headers = await getAuthHeaders();

  const res = await fetch(`${backendUrl}/api/pricing/config/${service}`, {
    method: 'GET',
    headers,
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch pricing config for ${service}`);
  }

  return res.json();
}

export async function updatePricingConfig(service, config) {
  const backendUrl = getBackendUrl();
  const headers = {
    'Content-Type': 'application/json',
    ...(await getAuthHeaders()),
  };

  const res = await fetch(
    `${backendUrl}/api/pricing/config/${service}/update`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ config }),
    }
  );

  if (!res.ok) {
    throw new Error(`Failed to update pricing config for ${service}`);
  }

  return res.json();
}

export async function calculatePricing(service, payload) {
  const backendUrl = getBackendUrl();
  const headers = {
    'Content-Type': 'application/json',
    ...(await getAuthHeaders()),
  };

  /*
   * Payload Normalization (Service-Agnostic)
   * 
   * The unified pricing endpoint expects: { data: {...}, discount }
   * Legacy callers (e.g., roof calculator) may send flat payloads: { sqFt, pitch, ..., discount }
   * 
   * This normalization ensures backward compatibility while standardizing
   * all pricing signals to the unified contract going forward.
   */
  let normalizedPayload;
  
  if (payload && typeof payload === 'object' && !payload.data) {
    // Flat payload detected — normalize to unified format
    const { discount = 0, ...data } = payload;
    normalizedPayload = { data, discount };
  } else {
    // Already in unified format or has explicit data key
    normalizedPayload = payload;
  }

  const res = await fetch(
    `${backendUrl}/api/pricing/calculate/${service}`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(normalizedPayload),
    }
  );

  if (!res.ok) {
    throw new Error(`Failed to calculate pricing for ${service}`);
  }

  const json = await res.json();

  // P1b — surface unconfigured-service state with an operator-friendly message.
  // The tenant has no saved pricing config for this service; the calculator
  // must not produce a customer-facing price.
  if (json && json.success === false && json.message === 'pricing_setup_required') {
    return {
      ...json,
      code: 'pricing_setup_required',
      setupRequired: true,
      message:
        "Pricing for this service isn't set up yet. Go to Settings → Calculator Settings to configure it before quoting.",
    };
  }

  return json;
}

/* Option B — explicitly copy starter values into the tenant's own pricing doc. */
export async function initStarterPricingConfig(service) {
  const backendUrl = getBackendUrl();
  const headers = {
    'Content-Type': 'application/json',
    ...(await getAuthHeaders()),
  };
  const res = await fetch(`${backendUrl}/api/pricing/config/${service}/init-starter`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    throw new Error(`Failed to load starter values for ${service}`);
  }
  return res.json();
}

/* =====================
   Backward compatibility exports
   ===================== */

// Used by PricingSettings.jsx
export async function fetchRoofPricingConfig() {
  return fetchPricingConfig('roof');
}

// Used by RoofEstimator.jsx
export async function calculateRoofPricingBackend(payload) {
  return calculatePricing('roof', payload);
}
